<script setup lang="ts">
import { computed, onMounted, shallowRef, useTemplateRef } from "vue"

import ContainerPicker from "@/components/ContainerPicker.vue"
import { useBulkCapture } from "@/composables/useBulkCapture"
import { ICON } from "@/lib/icons"
import type { BreadcrumbSegment, ContainerSummary } from "@/types/models"

import BulkCaptureControls from "./BulkCaptureControls.vue"
import BulkCaptureRejections from "./BulkCaptureRejections.vue"
import BulkCaptureStatus from "./BulkCaptureStatus.vue"
import BulkCaptureSummary from "./BulkCaptureSummary.vue"
import CaptureContextBar from "./CaptureContextBar.vue"
import CaptureViewfinder from "./CaptureViewfinder.vue"

const props = defineProps<{
  accountId: string
  containerId: string | null
  containerName: string | null
  containerAncestors: BreadcrumbSegment[]
  recentContainers: ContainerSummary[]
  allContainers: ContainerSummary[]
}>()

const emit = defineEmits<{
  exit: []
}>()

const selectedContainerId = shallowRef(props.containerId)
const selectedContainerName = shallowRef(props.containerName)
const selectedAncestors = shallowRef(props.containerAncestors)
const pickerOpen = shallowRef(false)
const nativePreview = shallowRef(false)
const cameraBusy = shallowRef(false)
const viewfinderRef = useTemplateRef<InstanceType<typeof CaptureViewfinder>>("viewfinderRef")
const flow = useBulkCapture({
  accountId: props.accountId,
  containerId: () => selectedContainerId.value,
})

const live = computed(() =>
  ["capturing", "closing", "blocked", "recovering", "uploading", "finishing"].includes(
    flow.phase.value,
  ),
)
const busy = computed(() =>
  ["closing", "blocked", "recovering", "finishing"].includes(flow.phase.value),
)
const showingResumePrompt = computed(
  () =>
    flow.phase.value === "resume" ||
    (flow.phase.value === "starting" && flow.resumableBatch.value !== null),
)
const displayedContainer = computed(() => {
  if (showingResumePrompt.value) return flow.resumableContainer.value
  if (live.value || flow.phase.value === "ready") return flow.batch.value?.container ?? null
  if (!selectedContainerId.value || !selectedContainerName.value) return null
  return { id: selectedContainerId.value, name: selectedContainerName.value }
})
const displayedContainerName = computed(() => displayedContainer.value?.name ?? null)
const displayedAncestors = computed(() =>
  displayedContainer.value?.id === selectedContainerId.value ? selectedAncestors.value : [],
)

onMounted(() => flow.initialize())

function handleCapture(): void {
  viewfinderRef.value?.openNativeCamera()
}

async function handlePhotosSelected(photos: File[]): Promise<void> {
  for (const photo of photos) await flow.capturePhoto(photo)
}

function handleContainerSelect(container: ContainerSummary): void {
  selectedContainerId.value = container.id
  selectedContainerName.value = container.name
  selectedAncestors.value = []
  pickerOpen.value = false
}

function handleContainerClear(): void {
  selectedContainerId.value = null
  selectedContainerName.value = null
  selectedAncestors.value = []
  pickerOpen.value = false
}

function handleOpenPicker(): void {
  if (flow.phase.value === "idle") pickerOpen.value = true
}
</script>

<template>
  <div
    class="fixed inset-0 flex w-full flex-col overflow-hidden"
    :class="nativePreview ? 'bg-transparent' : 'bg-black'"
  >
    <CaptureContextBar
      :ancestors="displayedAncestors"
      :container-name="displayedContainerName"
      @open-picker="handleOpenPicker"
      @back="emit('exit')"
    />

    <div v-if="flow.phase.value === 'loading'" class="flex flex-1 items-center justify-center">
      <UIcon :name="ICON.loading" class="size-8 animate-spin text-white" />
    </div>

    <div
      v-else-if="showingResumePrompt"
      data-testid="resume-prompt"
      class="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-white"
    >
      <UIcon :name="ICON.history" class="size-10 text-white/70" />
      <div class="text-center">
        <h1 class="text-xl font-semibold">Unfinished bulk capture</h1>
        <p class="mt-2 text-sm text-white/60">Continue where you left off or start a new batch.</p>
      </div>
      <div class="flex w-full max-w-sm flex-col gap-2">
        <UButton
          data-testid="resume-batch"
          size="xl"
          block
          :disabled="flow.phase.value === 'starting'"
          @click="flow.resume()"
        >
          Resume
        </UButton>
        <UButton
          data-testid="start-new-batch"
          size="xl"
          block
          color="neutral"
          variant="outline"
          :loading="flow.phase.value === 'starting'"
          :disabled="flow.phase.value === 'starting'"
          @click="flow.startNewBatch()"
        >
          Start new batch
        </UButton>
      </div>
    </div>

    <div
      v-else-if="flow.phase.value === 'idle' || flow.phase.value === 'starting'"
      class="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-white"
    >
      <UIcon :name="ICON.capture" class="size-12 text-white/70" />
      <div class="text-center">
        <h1 class="text-xl font-semibold">Bulk capture</h1>
        <p class="mt-2 max-w-sm text-sm text-white/60">
          Take one or more photos, then tap Next item. No typing required.
        </p>
      </div>
      <UButton
        size="xl"
        block
        class="max-w-sm"
        :loading="flow.phase.value === 'starting'"
        @click="flow.start()"
      >
        Start bulk capture
      </UButton>
      <UButton
        v-if="!selectedContainerId"
        color="neutral"
        variant="ghost"
        :icon="ICON.container"
        @click="handleOpenPicker"
      >
        Choose container
      </UButton>
      <p
        v-if="flow.error.value"
        role="alert"
        aria-live="assertive"
        class="text-error max-w-sm text-center text-sm"
      >
        {{ flow.error.value }}
      </p>
    </div>

    <BulkCaptureSummary
      v-else-if="flow.phase.value === 'ready' && flow.batch.value"
      :batch-id="flow.batch.value.id"
      :suggestions="flow.suggestions.value"
      @done="emit('exit')"
    />

    <div v-else-if="live" data-testid="bulk-live-capture" class="flex min-h-0 flex-1 flex-col">
      <div
        data-testid="bulk-status-region"
        class="relative z-10 flex shrink-0 flex-col gap-2 bg-black"
      >
        <BulkCaptureStatus
          :group-number="flow.groupNumber.value"
          :photo-count="flow.currentPhotoCount.value"
          :pending-count="flow.pendingCount.value"
          :thumbnails="flow.currentThumbnails.value"
          :error="flow.error.value"
          :queue-blocked="flow.queueBlocked.value"
        />
        <BulkCaptureRejections
          v-if="flow.rejectedPhotos.value.length"
          :rejections="flow.rejectedPhotos.value"
          @retry="flow.retryRejectedPhoto"
          @discard="flow.discardRejectedPhoto"
        />
      </div>

      <div data-testid="bulk-camera-region" class="relative min-h-0 flex-1">
        <CaptureViewfinder
          ref="viewfinderRef"
          :frozen="false"
          :scanner-active="false"
          zoom-position="above-capture-controls"
          multiple
          @photos-selected="handlePhotosSelected"
          @native-preview="nativePreview = $event"
          @update:busy="cameraBusy = $event"
        />
        <div data-testid="bulk-action-region" class="absolute inset-x-0 bottom-0 z-20">
          <BulkCaptureControls
            :can-capture="flow.canCapture.value && !cameraBusy"
            :can-start-next="flow.canStartNext.value"
            :can-finish="flow.canFinish.value"
            :can-retry-recovery="flow.canRetryRecovery.value"
            :can-replace-stale-queue="flow.canReplaceStaleQueue.value"
            :can-discard-stale-queue="flow.canDiscardStaleQueue.value"
            :busy="busy"
            @capture="handleCapture"
            @start-next="flow.startNext()"
            @retry-recovery="flow.retryRecovery()"
            @replace-stale-queue="flow.replaceStaleQueue()"
            @discard-stale-queue="flow.discardStaleQueue()"
            @finish="flow.finish()"
          />
        </div>
      </div>
    </div>

    <ContainerPicker
      v-if="pickerOpen && flow.phase.value === 'idle'"
      :open="pickerOpen"
      :recent-containers="recentContainers"
      :all-containers="allContainers"
      :searchable="false"
      allow-clear
      @update:open="pickerOpen = $event"
      @select="handleContainerSelect"
      @clear="handleContainerClear"
    />
  </div>
</template>
